import { secureHttp } from './api/secureHttp';
import { beHttp } from './api/beHttp';
// `import type` a propósito: estos módulos importan este adapter para pedir los
// datos, así que un import normal armaría un ciclo. Al ser solo tipos, TypeScript
// los borra en compilación y no queda ninguna dependencia en runtime. Los tipos
// se dejan donde ya estaban porque hay 12 archivos que los importan de ahí.
import type { SnapshotData } from './api/psychologist/snapshot';
import type {
  InactivityDetailData,
  InactivityRateData,
} from './api/psychologist/inactivity';

/** Respuesta de /api/psychologist/metrics/ranking. */
export interface RankingTransitionResponse {
  ranking_actual: string | null;
  ranking_anterior: string | null;
}

type SnapshotResponse = SnapshotData;
type InactivityRateResponse = InactivityRateData;
type InactivityDetailResponse = InactivityDetailData;

export interface PsychologistWorkSchedule {
  id: string;
  created_at: string;
  updated_at: string;
  psychologist_id: string;
  weekday: number;
  start_time: string;
  end_time: string;
  active: boolean;
}

export interface PsychologistWorkSchedulesResponse {
  results: PsychologistWorkSchedule[];
  metadata?: {
    total?: number;
    pages?: number;
    current_page?: number;
    per_page?: number;
    has_next_page?: boolean;
    has_prev_page?: boolean;
  };
}

export interface PsychologistWorkSchedulesQuery extends Record<string, unknown> {
  active?: boolean;
  weekday?: number;
  skip?: number;
  take?: number;
  page?: number;
  per_page?: number;
}

export interface CreateWorkSchedulePayload {
  weekday: number;
  start_time: string;
  end_time: string;
  active?: boolean;
  appointment_types?: string[];
}

export interface UpdateWorkSchedulePayload {
  weekday?: number;
  start_time?: string;
  end_time?: string;
  active?: boolean;
}

export interface CreateDirectSessionPayload {
  process_id: string;
  date: string;
  scheduled_by: string;
  notes?: string;
  appointment_type?: string;
}

export interface DirectSessionResponse {
  id: string;
  date: string;
  process_id: string;
  scheduled_by: string;
  notes?: string;
  status?: string;
  created_at?: string;
  appointment_type?: string;
}

export interface GenerateSlotsPayload {
  start_date: string;
  end_date: string;
  psychologist_ids: string[];
  all_psychologists?: boolean;
  appointment_types_by_psychologist?: Record<string, string[]>;
}

export interface GenerateSlotsResponse {
  total_slots_generated: number;
  psychologists_processed: number;
  slots_by_psychologist: Record<string, number>;
}

export type OnHoldReasonCategory = 'derivation_patient' | 'derivation_psychologist' | 'discharge';

export type NotificationLinkScope = "WHOLE" | "INLINE";

export interface PlatformNotification {
  /** id de la fila "destinataria" en psima — el que se usa para marcar como leída. */
  id: string;
  title: string | null;
  message: string;
  linkUrl: string | null;
  linkScope: NotificationLinkScope | null;
  linkText: string | null;
  sentAt: string;
  read: boolean;
  readAt: string | null;
}

export interface PlatformNotificationsResponse {
  notifications: PlatformNotification[];
}

export interface OnHoldReasonItem {
  process_id: string;
  patient_id: string;
  reason: OnHoldReasonCategory | null;
}

export default {
  async onHoldReasons(): Promise<{ items: OnHoldReasonItem[] }> {
    return await beHttp.get<{ items: OnHoldReasonItem[] }>(
      '/api/psychologist/patients/on-hold-reasons',
    );
  },

  async psychologistWorkSchedules(params?: PsychologistWorkSchedulesQuery): Promise<PsychologistWorkSchedulesResponse> {
    return await beHttp.get<PsychologistWorkSchedulesResponse>(
      '/api/psychologist/calendar/work-schedules',
      params,
    );
  },

  async patientsInformation(params?: { page?: number; per_page?: number; status?: string; therapy_type?: string }): Promise<{
    results: Array<{
      id: string;
      name: string | null;
      surname: string | null;
      therapy_type?: string | null;
      last_session_date?: string | null;
      next_session_date?: string | null;
    }>;
    metadata?: {
      total?: number;
      pages?: number;
      current_page?: number;
      per_page?: number;
      has_next_page?: boolean;
      has_prev_page?: boolean;
    };
  }> {
    const page = params?.page ?? 1;
    const per_page = params?.per_page ?? 10;
    const status = params?.status;
    const therapy_type = params?.therapy_type;
    const query: Record<string, string | number> = { page, per_page };
    if (status) query.status = status;
    if (therapy_type) query.therapy_type = therapy_type;
    return await beHttp.get(`/api/psychologist/patients/process-sessions-data`, query);
  },

  async createPsychologistWorkSchedule(data: CreateWorkSchedulePayload): Promise<PsychologistWorkSchedule> {
    return await beHttp.post<PsychologistWorkSchedule>(
      '/api/psychologist/calendar/work-schedules',
      data,
    );
  },

  async updatePsychologistWorkSchedule(id: string, data: UpdateWorkSchedulePayload): Promise<PsychologistWorkSchedule> {
    return await beHttp.put<PsychologistWorkSchedule>(
      `/api/psychologist/calendar/work-schedules/${id}`,
      data,
    );
  },

  async deletePsychologistWorkSchedule(id: string): Promise<void> {
    return await beHttp.delete<void>(
      `/api/psychologist/calendar/work-schedules/${id}`,
    );
  },

  async createDirectSession(data: CreateDirectSessionPayload): Promise<DirectSessionResponse> {
    // createDirectSession es CORE — sigue en api-core (deprecated, preferir scheduleSession).
    return await secureHttp.post<DirectSessionResponse, CreateDirectSessionPayload>(
      '/v1/psi-platform/session/direct',
      data,
    );
  },

  async generateSlotsFromWorkSchedules(data: GenerateSlotsPayload): Promise<GenerateSlotsResponse> {
    return await beHttp.post<GenerateSlotsResponse>(
      '/api/psychologist/calendar/work-schedules/generate-slots',
      data,
    );
  },

  /** Polling liviano para el numerito de la campana — sin toast de error (se reintenta solo). */
  async notificationsUnreadCount(): Promise<{ count: number }> {
    return await beHttp.get<{ count: number }>(
      '/api/psychologist/notifications/unread-count',
      undefined,
      { showErrorToast: false },
    );
  },

  /** Detalle completo — se pide lazy, recién cuando se abre el dropdown. */
  async notifications(): Promise<PlatformNotificationsResponse> {
    return await beHttp.get<PlatformNotificationsResponse>(
      '/api/psychologist/notifications',
      undefined,
      { showErrorToast: false },
    );
  },

  async markNotificationAsRead(id: string): Promise<{ ok: true }> {
    return await beHttp.patch<{ ok: true }>(
      `/api/psychologist/notifications/${id}/read`,
      undefined,
      { showErrorToast: false },
    );
  },

  // ---------------------------------------------------------------------------
  // Métricas de la psicóloga (ranking, cupo, inactividad)
  // ---------------------------------------------------------------------------
  // Antes estos cuatro datos venían de webhooks de Make llamados directo desde
  // el frontend, con la URL hardcodeada en `src/`. Eso publicaba la URL en el
  // bundle (el token del path de un webhook de Make ES la credencial) y, como
  // esos webhooks toman `psychologist_id` por parámetro y no tienen auth, se
  // podían leer las métricas de cualquier psicóloga — el detalle de inactividad
  // incluye nombres de pacientes.
  //
  // Ahora los sirve psiplatform-be con el id sacado del JWT: no hace falta
  // (ni se puede) mandar un psychologistId. Si en algún momento se agrega uno
  // como parámetro acá, se reabre ese agujero.

  async metricsRanking(): Promise<RankingTransitionResponse> {
    return await beHttp.get<RankingTransitionResponse>(
      '/api/psychologist/metrics/ranking',
    );
  },

  async metricsSnapshot(): Promise<SnapshotResponse> {
    return await beHttp.get<SnapshotResponse>(
      '/api/psychologist/metrics/snapshot',
    );
  },

  /** @param month `YYYY-MM` */
  async metricsInactivityRate(month: string): Promise<InactivityRateResponse> {
    return await beHttp.get<InactivityRateResponse>(
      '/api/psychologist/metrics/inactivity',
      { month },
      // Un mes sin fila en la hoja (psicóloga fuera del roster ese mes) responde 503; la UI ya
      // lo muestra como "Sin datos", así que el toast de error solo mete ruido.
      { showErrorToast: false },
    );
  },

  /**
   * @param month `MM`
   * @param year  `YYYY`
   */
  async metricsInactivityDetail(
    month: string,
    year: string,
  ): Promise<InactivityDetailResponse> {
    return await beHttp.get<InactivityDetailResponse>(
      '/api/psychologist/metrics/inactivity-detail',
      { month, year },
    );
  },
};

